import Validator from 'jsonschema/lib/validator';
import { merge } from 'lodash-es';
import {
  getUrlPathIndex,
  isActivePage,
  hrefWithBasename,
  stringifyUrlParams,
  withUrlPrefix,
} from 'platform/forms-system/src/js/helpers';
import { isMinimalHeaderPath } from 'platform/forms-system/src/js/patterns/minimal-header';
import {
  defaultFocusSelector,
  focusPageHeader,
} from 'platform/utilities/ui/focus';
import { scrollTo, scrollToTop } from 'platform/utilities/scroll';
import { getEligiblePages } from 'platform/forms-system/src/js/routing';
import navigationState from 'platform/forms-system/src/js/utilities/navigation/navigationState';
import {
  errorSchemaIsValid,
  uiSchemaValidate,
} from 'platform/forms-system/src/js/validation';
import get from 'platform/utilities/data/get';
import set from 'platform/utilities/data/set';
import environment from 'platform/utilities/environment';
import {
  dispatchDuplicateItemError,
  dispatchIncompleteItemError,
} from './ArrayBuilderEvents';
import { DEFAULT_ARRAY_BUILDER_TEXT } from './arrayBuilderText';

// Previously set to '_metadata', but upon saving, the Ruby gem 'olivebranch'
// converts this to 'Metadata', then upon returning to the form, this key is
// converted to 'metadata'. To avoid confusion, we're using 'metadata'; and this
// may need to be filtered out of submission data.
export const META_DATA_KEY = 'metadata';

export { hrefWithBasename, withUrlPrefix };

/**
 * @param {string} [search] e.g. `?add=true`
 * @returns {URLSearchParams}
 */
export function getArrayUrlSearchParams(search = window?.location?.search) {
  return new URLSearchParams(search);
}

/**
 * Initializes the getText function for the ArrayBuilder
 */
export function initGetText({
  textOverrides = {},
  getItemName,
  nounPlural,
  nounSingular,
} = {}) {
  if (!getItemName || typeof getItemName !== 'function') {
    throw new Error('getItemName is required for initGetText');
  }
  if (!nounPlural) {
    throw new Error('nounPlural is required for initGetText');
  }
  if (!nounSingular) {
    throw new Error('nounSingular is required for initGetText');
  }
  const getTextValues = {
    ...DEFAULT_ARRAY_BUILDER_TEXT,
    getItemName,
    ...textOverrides,
  };

  const searchParams = getArrayUrlSearchParams();
  const getTextProps = {
    getItemName,
    nounPlural,
    nounSingular,
    isEdit: !!searchParams.get('edit'),
    isAdd: !!searchParams.get('add'),
  };

  /**
   * @param {ArrayBuilderTextKey} key
   * @param {any} itemData
   * @param {any} [formData]
   * @param {number} [index]
   * @returns {string}
   */
  return (key, itemData, formData, index) => {
    const keyVal = getTextValues?.[key];

    if (key === 'getItemName') {
      return typeof keyVal === 'function'
        ? keyVal(itemData, index, formData)
        : keyVal;
    }

    if (key === 'cardDescription') {
      let text = keyVal;

      if (typeof keyVal === 'function') {
        // a try/catch is already done for getItemName in arrayBuilder.jsx,
        // so only handle cardDescription here, possibly extend this to
        // other text functions in the future
        try {
          text = keyVal(itemData, index, formData);
        } catch (e) {
          text = '';
          if (!environment.isProduction()) {
            // eslint-disable-next-line no-console
            console.error(
              `Error in array builder option "cardDescription":`,
              e,
            );
          }
        }
      }
      return text;
    }

    return typeof keyVal === 'function'
      ? getTextValues?.[key]({
          ...getTextProps,
          itemData,
          formData,
          index,
        })
      : keyVal;
  };
}

/**
 * introRoute is optional, if not provided, it will default to summaryRoute
 *
 * Usage:
 * ```
 * onNavBack: onNavBackRemoveAddingItem({
 *  arrayPath: 'employers',
 *  getSummaryPath: formData => 'array-multiple-page-builder-summary',
 *  getIntroPath: formData => 'array-multiple-page-builder',
 * }),
 * ```
 * @param {{
 *  arrayPath: string;
 *  getSummaryPath: (formData: any) => string;
 *  getIntroPath: (formData: any) => string;
 *  reviewRoute: string;
 * }} props
 */
export function onNavBackRemoveAddingItem({
  arrayPath,
  getIntroPath,
  getSummaryPath,
  reviewRoute,
}) {
  return function onNavBack({
    goPath,
    formData,
    setFormData,
    urlParams,
    urlPrefix,
  }) {
    const arrayData = get(arrayPath, formData);
    let newArrayData = arrayData;
    if ('add' in urlParams && arrayData?.length) {
      // remove current item
      newArrayData = arrayData.slice(0, -1);
      const newData = set(arrayPath, newArrayData, formData);
      setFormData(newData);
    }

    const summaryRoute = getSummaryPath?.(formData);
    const introRoute = getIntroPath?.(formData);
    let path = introRoute && !newArrayData?.length ? introRoute : summaryRoute;
    if (urlParams?.review) {
      path = reviewRoute;
    }
    goPath(withUrlPrefix(path, urlPrefix));
  };
}

/**
 * Usage:
 * ```
 * uiSchema: ...
 * schema: ...
 * onNavForward: onNavForwardKeepUrlParams,
 * ```
 */
export function onNavForwardKeepUrlParams({ goNextPath, urlParams }) {
  goNextPath(urlParams);
}

/**
 * Navigate back while preserving URL query params.
 *
 * If currently on a per-item page, uses the default `goPreviousPath(urlParams)`.
 * Otherwise, when backing from outside a per-item loop, it searches backward to
 * the nearest array summary page and navigates there (keeping params). On error
 * or if no target is found, falls back to `goPreviousPath(urlParams)`.
 *
 * @param {Object} args
 * @param {Object} args.formData - Current form data used to resolve eligible pages.
 * @param {(path:string)=>void} args.goPath - Function to navigate to a specific path.
 * @param {(params?:Object)=>any} args.goPreviousPath - Function to navigate to the previous path.
 * @param {Array<Object>} args.pageList - Ordered list of page configs for the flow.
 * @param {string} args.pathname - Current route pathname.
 * @param {Record<string, string | number | boolean | (string|number|boolean)[]>} args.urlParams - URL params to preserve.
 * @returns {any} Whatever the navigation function returns (often `void`).
 *
 * @example // Usage in a page config
 * const page = {
 *   uiSchema,
 *   schema,
 *   onNavBack: onNavBackKeepUrlParams,
 * };
 */
export function onNavBackKeepUrlParams({
  formData,
  goPath,
  goPreviousPath,
  pageList,
  pathname,
  urlParams,
}) {
  try {
    const { pages, pageIndex } = getEligiblePages(pageList, formData, pathname);
    if (pageIndex > 0) {
      const isPerItem = p =>
        !!(p?.showPagePerItem || p?.pageConfig?.showPagePerItem);

      const current = pages[pageIndex];
      const prev = pages[pageIndex - 1];

      // we're inside the loop, use the default behavior
      if (isPerItem(current)) return goPreviousPath(urlParams);

      // we're outside the loop, find the nearest page that is not a per-item page
      if (isPerItem(prev)) {
        let i = pageIndex - 1;
        while (i >= 0 && !pages[i]?.isArrayBuilderSummary) i -= 1;

        if (i >= 0 && pages[i]?.path) {
          const params = stringifyUrlParams(urlParams);
          return goPath(pages[i].path + (params || ''));
        }
      }
    }
  } catch {
    /* ignore and fall back */
  }

  return goPreviousPath(urlParams);
}

/**
 * Creates a path with a `add` query param
 * @param {Object} props
 * @param {string} props.path e.g. `/path-item/:index`
 * @param {boolean} [props.isReview] if coming from the review page
 * @param {boolean} [props.removedAllWarn] if all items were removed
 * @param {string | number} props.index
 * @returns {string} e.g. `/path-item/0?add=true`
 */
export function createArrayBuilderItemAddPath({
  path,
  index,
  isReview,
  removedAllWarn,
}) {
  return `${path.replace(':index', index)}?add=true${
    isReview ? '&review=true' : ''
  }${removedAllWarn ? '&removedAllWarn=true' : ''}`;
}

/**
 * Creates a path with a `edit` query param
 * @param {Object} props
 * @param {string} props.path e.g. `/path-item/:index`
 * @param {string | number} props.index
 * @param {boolean} [props.isReview] if coming from the review page
 * @returns {string} e.g. `/path-item/0?edit=true`
 */
export function createArrayBuilderItemEditPath({ path, index, isReview }) {
  return `${path.replace(':index', index)}?edit=true${
    isReview ? '&review=true' : ''
  }`;
}

export function slugifyText(text, { convertCamelCase = true } = {}) {
  if (!text) return '';
  let result = text;

  if (convertCamelCase) {
    result = result.replace(/([a-z])([A-Z])/g, '$1-$2');
  }

  return result.toLowerCase().replace(/ /g, '-');
}

/**
 * Creates a path with a `updated` query param
 * @param {Object} props
 * @param {string} props.basePath e.g. `/path-summary`
 * @param {string} props.arrayPath e.g. `employers`
 * @param {string | number} props.index
 * @returns {string} e.g. `/path-summary?updated=employers-0`
 */
export function createArrayBuilderUpdatedPath({ basePath, arrayPath, index }) {
  return `${basePath}?updated=${slugifyText(arrayPath)}-${index}`;
}

export function isDeepEmpty(obj) {
  return obj
    ? Object.values(obj).every(
        value =>
          (typeof value === 'object' && value !== null && isDeepEmpty(value)) ||
          value === null ||
          value === undefined ||
          value === '',
      )
    : true;
}

// Used as a helper so that we can stub this for tests
export function getArrayIndexFromPathName(
  pathname = window?.location?.pathname,
) {
  return getUrlPathIndex(pathname);
}

/**
 * Gets the arrayPathSlug and index from the URL `updated=` query parameter
 * @param {string} [search] e.g. `?updated=employers-0`
 * @returns {{
 *   arrayPathSlug: string | null,
 *   index: number | null,
 * }}
 */
export function getUpdatedItemFromPath(search = window?.location?.search) {
  const urlParams = getArrayUrlSearchParams(search);
  const updatedValue = urlParams.get('updated');

  const updatedItem = {
    arrayPathSlug: null,
    index: null,
  };

  try {
    const parts = updatedValue?.split('-');
    if (parts?.length) {
      const index = Number(parts.pop());
      updatedItem.index = index;
      updatedItem.arrayPathSlug = parts.join('-');
    }
  } catch (e) {
    // do nothing
  }
  return updatedItem;
}

/**
 * @param {{
 *  arrayPath: string,
 *  nounSingular: string,
 *  nounPlural: string,
 *  maxItems: boolean,
 * }} options
 */
export const maxItemsHint = ({
  arrayData,
  nounSingular,
  nounPlural,
  maxItems,
}) => {
  let hint = '';
  const len = arrayData?.length;

  if (maxItems) {
    if (!len || maxItems === 1 || len >= maxItems) {
      hint = `You can add up to ${maxItems}.`;
    } else if (len && maxItems - len === 1) {
      hint = `You can add 1 more ${nounSingular}.`;
    } else if (len && maxItems - len > 1) {
      hint = `You can add ${maxItems - len} more ${nounPlural}.`;
    }
  }

  return hint;
};

const scrollAndFocusPageHeader = () => {
  const minimalHeader = isMinimalHeaderPath();
  const headerLevel = minimalHeader ? 1 : 3;

  if (minimalHeader) {
    scrollTo('header-minimal');
  } else {
    scrollToTop('topScrollElement');
  }

  focusPageHeader(headerLevel, [`form h${headerLevel}`, defaultFocusSelector]);
};

export const defaultSummaryPageScrollAndFocusTarget = scrollAndFocusPageHeader;

export const defaultItemPageScrollAndFocusTarget = scrollAndFocusPageHeader;

export const replaceItemInFormData = ({
  formData,
  newItem,
  arrayPath,
  index,
}) => {
  let newFormData = formData;

  if (formData?.[arrayPath]?.[index]) {
    newFormData = {
      ...formData,
      [arrayPath]: formData[arrayPath].map((item, i) => {
        return i === index ? newItem : item;
      }),
    };
  }

  return newFormData;
};

export const arrayBuilderContextObject = ({
  add = false,
  edit = false,
  review = false,
}) => {
  return { add, edit, review };
};

export const arrayBuilderDependsContextWrapper = contextObject => {
  let add = false;
  let edit = false;
  let review = false;

  if (contextObject) {
    add = contextObject.add;
    edit = contextObject.edit;
    review = contextObject.review;
  } else {
    const urlParams = getArrayUrlSearchParams();
    add = urlParams.get('add') === 'true';
    edit = urlParams.get('edit') === 'true';
    review = urlParams.get('review') === 'true';
  }

  return { add, edit, review };
};

/**
 * Validates an array item against all its active item page schemas.
 * Mimics the Review Page validation logic to ensure consistency.
 *
 * @param {Object} params
 * @param {any} params.itemData - The data for a single array item
 * @param {number} params.itemIndex - The index of the item in the array
 * @param {Object} params.fullData - The complete form data
 * @param {string} params.arrayPath - Path to the array in form data (e.g., 'employers')
 * @param {Object} params.formPages - All form pages from Redux state (state.form.pages)
 * @param {Object} [params.appStateData] - Additional app state data for validation
 * @returns {boolean} - True if item is valid, false if invalid
 */
export const validateItemAgainstPages = ({
  itemData,
  itemIndex,
  fullData,
  arrayPath,
  formPages = {},
  appStateData = {},
}) => {
  if (!itemData) return true;

  const itemPages = Object.values(formPages).filter(
    page => page.showPagePerItem && page.arrayPath === arrayPath,
  );

  if (!itemPages.length) return true;

  const getItemSchema = schema => {
    const arraySchema = schema?.properties?.[arrayPath];
    if (!arraySchema) return null;

    const { items, additionalItems } = arraySchema;
    return Array.isArray(items) ? items[itemIndex] || additionalItems : items;
  };

  const uiSchemaIsValid = ({ itemUiSchema, itemSchema }) => {
    if (!itemUiSchema) return true;

    const customErrors = {
      __errors: [],
      addError(msg) {
        this.__errors.push(msg);
      },
    };

    uiSchemaValidate(
      customErrors,
      itemUiSchema,
      itemSchema,
      itemData,
      '',
      null,
      appStateData,
      () => fullData,
    );

    return errorSchemaIsValid(customErrors);
  };

  const validator = new Validator();
  return itemPages.every(page => {
    const pageWithIndex = { ...page, index: itemIndex };
    if (!isActivePage(pageWithIndex, fullData)) return true;

    const itemSchema = getItemSchema(page.schema);
    if (!itemSchema) return true;

    const { valid } = validator.validate(itemData, itemSchema);
    if (!valid) return false;

    return uiSchemaIsValid({
      itemUiSchema: page.uiSchema?.[arrayPath]?.items,
      itemSchema,
    });
  });
};

/**
 * Returns an effective isItemIncomplete function.
 * If one is provided in options, uses that. Otherwise, creates a wrapper
 * around schema validation.
 *
 * @param {Object} params
 * @param {Function|undefined} params.isItemIncomplete - Custom incomplete check function
 * @param {string} params.arrayPath - Path to array in form data
 * @param {Object} params.formPages - All form pages from Redux state
 * @param {Object} params.appStateData - Application state data for custom validations
 * @returns {Function} (item, index, fullData) => boolean - Returns true if item is incomplete
 */
export const getEffectiveIsItemIncomplete = ({
  isItemIncomplete,
  arrayPath,
  formPages,
  appStateData,
}) => {
  if (isItemIncomplete) {
    return (item, _index, fullData) => isItemIncomplete(item, fullData);
  }

  const hasItemPages = Object.values(formPages || {}).some(
    page => page.showPagePerItem && page.arrayPath === arrayPath,
  );

  if (!hasItemPages) return () => false;

  return (item, index, fullData) => {
    let itemIndex = index;

    if (itemIndex === undefined && fullData?.[arrayPath]) {
      itemIndex = fullData[arrayPath].indexOf(item);
      if (itemIndex === -1) return true;
    }

    return !validateItemAgainstPages({
      itemData: item,
      itemIndex,
      fullData,
      arrayPath,
      formPages,
      appStateData,
    });
  };
};

/**
 * Validates array items for completeness.
 * Falls back to schema validation if isItemIncomplete is not provided.
 *
 * @param {Object} props
 * @param {Array} props.arrayData - The array data to validate
 * @param {string} [props.arrayPath] - The array path (e.g., 'employers')
 * @param {Object} [props.appStateData] - Additional app state data for validation
 * @param {Object} props.fullData - The complete form data
 * @param {Object} [props.formPages] - Form pages from Redux state for schema validation
 * @param {ArrayBuilderOptions['isItemIncomplete']} [props.isItemIncomplete] - Optional custom validation function
 * @returns {boolean} - Returns true if all items are complete/valid
 */
export const validateIncompleteItems = ({
  arrayData,
  arrayPath,
  appStateData,
  formPages,
  fullData,
  isItemIncomplete,
}) => {
  const effectiveIsItemIncomplete = getEffectiveIsItemIncomplete({
    arrayPath,
    formPages,
    appStateData,
    isItemIncomplete,
  });

  const invalidIndex = Array.isArray(arrayData)
    ? arrayData.findIndex((item, index) =>
        effectiveIsItemIncomplete(item, index, fullData),
      )
    : -1;
  const isValid = invalidIndex === -1;

  if (!isValid && navigationState.getNavigationEventStatus()) {
    dispatchIncompleteItemError({
      index: invalidIndex,
      arrayPath,
    });
  }

  return isValid;
};

/**
 * Adds array item completeness validation to an Array Builder summary page.
 * Registers a hidden validation field and a page-level validation callback
 * that blocks submission/navigation when any array item is incomplete.
 *
 * @param {Object} params
 * @param {string} params.arrayPath - The array path (e.g., 'employers')
 * @param {ArrayBuilderOptions['isItemIncomplete']} [params.isItemIncomplete] - Optional custom validation function
 * @param {PageSchema} params.page - The page schema to add validation to
 * @returns {PageSchema} - The page schema with validation added
 */
export const applyArrayBuilderValidation = ({
  arrayPath,
  isItemIncomplete,
  page,
}) => {
  const validationKey = `view:${arrayPath}Validation`;

  const validateArrayItems = (
    errors,
    _fieldData,
    formData,
    _schema,
    _errorMessages,
    _currentIndex,
    appStateData,
  ) => {
    const isValid = validateIncompleteItems({
      arrayData: get(arrayPath, formData),
      isItemIncomplete,
      arrayPath,
      fullData: formData,
      formPages: appStateData?.formPages,
      appStateData,
    });

    if (!isValid) errors[validationKey]?.addError('');
  };

  return merge({}, page, {
    schema: {
      properties: {
        [validationKey]: { type: 'object', properties: {}, 'ui:hidden': true },
      },
    },
    uiSchema: {
      [validationKey]: {
        'ui:title': ' ',
        'ui:options': { showFieldLabel: 'no-wrap', hideIf: () => true },
      },
      'ui:validations': [
        ...(page.uiSchema?.['ui:validations'] || []),
        validateArrayItems,
      ],
    },
  });
};

/**
 * Resolves `maxItems` to a numeric value.
 *
 * - If `maxItems` is a function, it is called with `formData` and the returned
 *   value is validated as a number.
 * - If `maxItems` is a number, it is validated as finite and returned.
 * - If `maxItems` is a string, it is trimmed, parsed into a number, and validated.
 *
 * @param {number | string | ((formData: object) => number | string)} maxItems
 *   A static limit, string value, or resolver function.
 * @param {object} formData
 *   Data passed to the resolver when `maxItems` is a function.
 * @returns {number | undefined}
 *   The resolved maximum item count, or `undefined` if invalid or an error occurs.
 */
export const maxItemsFn = (maxItems, formData = {}) => {
  try {
    const raw = typeof maxItems === 'function' ? maxItems(formData) : maxItems;
    const value = typeof raw === 'string' ? Number(raw.trim()) : raw;
    return typeof value === 'number' && Number.isFinite(value)
      ? value
      : undefined;
  } catch {
    return undefined;
  }
};

/*
 * Process array data for duplicate comparison
 * @param {Array<String>} array - array of processed form data for comparison
 * @returns {String} - Combined processed form data
 */
export const processArrayData = array => {
  if (!Array.isArray(array)) {
    throw new Error('Processing array data requires an array', array);
  }
  // Make sure we're not slugifying strings with only spaces
  return slugifyText(
    array.map(item => (item || '').toString().trim()).join(';'),
    { convertCamelCase: false },
  );
};

/**
 * Get item data from the array based on duplicate check settings
 * @param {string} arrayPath - The path to the array in the form data
 * @param {DuplicateChecks} duplicateChecks - The duplicate checks object
 * @param {Object} fullData - The full form data
 * @param {Number} itemIndex - The index of the item in the array
 * @returns {String} - A string representing item data for duplicate checking
 */
export const getItemDataFromPath = ({
  arrayPath,
  duplicateChecks = {},
  itemIndex,
  fullData,
}) =>
  processArrayData(
    (duplicateChecks.comparisons || []).map(path =>
      get([arrayPath, itemIndex, ...path.split('.')], fullData),
    ),
  );

/**
 * Metadata key for duplicate item tracking
 * @param {string} arrayPath - The path to the array in the form data
 * @param {DuplicateChecks} duplicateChecks - The duplicate checks object
 * @param {Number} itemIndex - The index of the item in the array
 * @param {Array<String>} [itemArray] - array of item data for used in summary
 * cards
 * @param {Object} formData - The full form data
 * @returns {String} - The metadata key for the item
 */
export const getItemDuplicateDismissedName = ({
  arrayPath,
  duplicateChecks,
  itemIndex,
  itemString = '',
  fullData = {},
}) => {
  const data =
    itemString ||
    getItemDataFromPath({
      arrayPath,
      duplicateChecks,
      itemIndex,
      fullData,
    }) ||
    '';

  return data ? [arrayPath, data, 'allowDuplicate'].join(';') : '';
};

/**
 * Get array data using duplicate checks comparisons
 * @param {string} arrayPath - The path to the array in the form data
 * @param {DuplicateChecks} duplicateChecks - The duplicate checks object
 * @param {Object} formData - The full form data
 * @returns
 */
export const getArrayDataFromDuplicateChecks = ({
  arrayPath,
  duplicateChecks,
  fullData,
}) => {
  const arrayLength = get(arrayPath, fullData)?.length || 0;

  // Build an array of data values from provided paths
  return new Array(arrayLength).fill([]).map((_, itemIndex) =>
    getItemDataFromPath({
      arrayPath,
      duplicateChecks,
      itemIndex,
      fullData,
    }),
  );
};

/**
 * @typedef {Object} DuplicateCheckResult
 * @property {Array<String>} arrayData - The array data being checked for duplicates
 * @property {Array<String>} duplicates - The list of duplicate items found
 * @property {boolean} hasDuplicate - Indicates if any duplicates were found
 * @property {Array<String>} externalComparisonData - The external comparison data
 */
/**
 * Utility function to check for duplicates in an array based on object keys
 * @param {string} arrayPath - The path to the array in the form data
 * @param {DuplicateChecks} checks - An object containing array paths of data
 * that needs to be checked for duplicates
 * @param {Object} fullData - Full form data to check for duplicates
 * @returns {DuplicateCheckResult}
 */
export const checkIfArrayHasDuplicateData = ({
  arrayPath,
  duplicateChecks = {},
  fullData,
}) => {
  const arrayData = getArrayDataFromDuplicateChecks({
    arrayPath,
    duplicateChecks,
    fullData,
  });

  let externalComparisonData = [];
  // Get external comparison data; fallback to include both internal & external
  // if comparisonType is not set to exclusively check internal data
  if (
    duplicateChecks.comparisonType !== 'internal' &&
    typeof duplicateChecks.externalComparisonData === 'function'
  ) {
    externalComparisonData = duplicateChecks.externalComparisonData({
      formData: fullData,
      arrayData,
    });
  }

  // Join all data & strip out empty strings & arrays of empty strings
  const internalComparisonData =
    duplicateChecks.comparisonType === 'external'
      ? new Set(arrayData) // ignore internal duplicates
      : arrayData;
  const allItems = [
    ...internalComparisonData,
    ...externalComparisonData.map(processArrayData),
  ].filter(item => item.replace(/;/g, '').length > 0);
  const duplicates = allItems.filter(
    (item, itemIndex) => allItems.indexOf(item) !== itemIndex,
  );
  const hasDuplicate = new Set(allItems).size !== allItems.length;

  if (hasDuplicate) {
    dispatchDuplicateItemError({
      index: arrayData.indexOf(duplicates[0]),
      arrayPath,
    });
  }

  return {
    arrayData,
    duplicates,
    externalComparisonData,
    hasDuplicate,
  };
};

export const defaultDuplicateResult = {
  arrayData: [],
  hasDuplicate: false,
  duplicates: [],
  externalComparisonData: [],
};

export const checkForDuplicatesInItemPages = ({
  arrayPath,
  duplicateChecks,
  fullData,
  index,
  itemData,
}) => {
  const itemDuplicateDismissedName = getItemDuplicateDismissedName({
    arrayPath,
    duplicateChecks,
    fullData,
    itemIndex: index || 0,
  });

  const newData = { ...fullData, [arrayPath]: fullData[arrayPath] };
  newData[arrayPath][index] = itemData;

  if (
    !duplicateChecks ||
    newData[META_DATA_KEY]?.[itemDuplicateDismissedName] ||
    !(
      duplicateChecks.externalComparisonData ||
      duplicateChecks.comparisons?.length > 0
    )
  ) {
    return defaultDuplicateResult;
  }
  return checkIfArrayHasDuplicateData({
    arrayPath,
    duplicateChecks,
    fullData: newData,
    index,
  });
};

export const getDependsPath = (pages, formData) => {
  if (!pages || !pages.length) {
    return null;
  }

  if (pages.length === 1) {
    return pages[0].path;
  }

  for (let i = 0; i < pages.length; i++) {
    if (pages[i]?.depends(formData)) {
      return pages[i].path;
    }
  }

  return pages[0].path;
};
